import type { DateRange } from "@repo/app-types/entities/dateRange";
import type { GroupId } from "@repo/app-types/entities/group";
import type { PlaybookId } from "@repo/app-types/entities/playbook";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Label } from "@repo/ui/Label/label";
import { Modal } from "@repo/ui/Modal/modal";
import { ModalHeader } from "@repo/ui/Modal/modalHeader";
import {
  SimpleSelect,
  type SimpleSelectOption,
} from "@repo/ui/Select/simpleSelect";
import { Size } from "@repo/ui/helpers/helpers";
import { type FC, useEffect, useMemo, useState } from "react";
import { useAllMinimalPlaybooksQuery } from "../../../api/entities/playbook.js";
import { DateRangeFilterItem } from "../../filters/dateRangeFilterItem.js";
import { TeammateGroupFilterItemContainer } from "../../filters/teammateGroupFilterItemContainer.js";

export interface TestSignalFiltersUi {
  playbookId: PlaybookId | null;
  // `null` means "no teammate-group filter applied" (equivalent to "All
  // teammates"). An array (possibly empty) is an explicit multi-select.
  teammateGroupIds: readonly GroupId[] | null;
  dateRange: DateRange;
}

export interface SignalTestConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialFilters: TestSignalFiltersUi;
  onRunTest: (filters: TestSignalFiltersUi) => void;
}

export const SignalTestConfigModal: FC<SignalTestConfigModalProps> = ({
  isOpen,
  onClose,
  initialFilters,
  onRunTest,
}) => {
  const [filters, setFilters] = useState<TestSignalFiltersUi>(initialFilters);

  // Re-seed local state from initialFilters every time the modal opens so
  // reopening the dialog always reflects the latest within-session selections.
  useEffect(() => {
    if (isOpen) {
      setFilters(initialFilters);
    }
  }, [isOpen, initialFilters]);

  const { data: playbooks } = useAllMinimalPlaybooksQuery();

  const playbookOptions = useMemo<readonly SimpleSelectOption<PlaybookId>[]>(
    () => playbooks?.map((p) => ({ label: p.name, value: p.id })) ?? [],
    [playbooks],
  );

  return (
    <Modal isOpen={isOpen} onCancel={onClose}>
      <ModalHeader onClose={onClose}>Test configuration</ModalHeader>

      <div className="mt-6 flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <Label size={Size.SMALL}>Playbook</Label>
          <SimpleSelect
            label="Playbook"
            isLabelHidden
            placeholder="All playbooks"
            value={filters.playbookId}
            options={playbookOptions}
            onChange={(value) => setFilters({ ...filters, playbookId: value })}
          />
        </div>

        <div className="flex flex-col gap-1">
          <Label size={Size.SMALL}>Time range</Label>
          <DateRangeFilterItem
            hasBasisPicker={false}
            value={filters.dateRange}
            onChange={(dateRange) => setFilters({ ...filters, dateRange })}
          />
        </div>

        <div className="flex flex-col gap-1">
          <Label size={Size.SMALL}>Teammate group</Label>
          <TeammateGroupFilterItemContainer
            value={filters.teammateGroupIds}
            onChange={(teammateGroupIds) =>
              setFilters({ ...filters, teammateGroupIds })
            }
          />
        </div>
      </div>

      <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
        <div className="ml-3">
          <Button
            variant={ButtonVariant.Primary}
            onClick={() => onRunTest(filters)}
          >
            Run test
          </Button>
        </div>
        <Button variant={ButtonVariant.Secondary} onClick={onClose}>
          Close
        </Button>
      </div>
    </Modal>
  );
};
